/**
 * Authentication against the portal API.
 *
 * Requests here use plain fetch rather than the shared apiClient on purpose:
 * apiClient depends on this module for its Authorization header, and the
 * refresh call must not re-enter the client's own refresh interceptor.
 */

import { API_BASE, readErrorMessage } from './api-base';
import type { components } from '../types/api.generated';

type TokenResponse = components['schemas']['TokenResponse'];
type UserResponse = components['schemas']['UserResponse'];

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'user';
  avatar?: string | null;
  phone?: string | null;
  emailVerified: boolean;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// Token storage keys
const ACCESS_TOKEN_KEY = 'lba_access_token';
const REFRESH_TOKEN_KEY = 'lba_refresh_token';
const USER_KEY = 'lba_user';
const EXPIRES_KEY = 'lba_token_expires';

// Token refresh threshold (5 minutes before expiry)
const REFRESH_THRESHOLD = 5 * 60 * 1000;

/** Map the API user onto the shape the app renders. */
export function toUser(payload: UserResponse): User {
  return {
    id: payload.id,
    email: payload.email,
    name: payload.name,
    role: payload.role === 'admin' ? 'admin' : 'user',
    avatar: payload.avatar ?? null,
    phone: payload.phone ?? null,
    emailVerified: payload.email_verified,
  };
}

class AuthService {
  private refreshPromise: Promise<AuthTokens> | null = null;

  /**
   * Get stored access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY) || sessionStorage.getItem(ACCESS_TOKEN_KEY);
  }

  /**
   * Get stored refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY) || sessionStorage.getItem(REFRESH_TOKEN_KEY);
  }

  /**
   * Get stored user
   */
  getUser(): User | null {
    const userStr = localStorage.getItem(USER_KEY) || sessionStorage.getItem(USER_KEY);
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  /**
   * Check if token is expired or about to expire
   */
  isTokenExpired(): boolean {
    const expiresStr = localStorage.getItem(EXPIRES_KEY) || sessionStorage.getItem(EXPIRES_KEY);
    if (!expiresStr) return true;
    const expiresAt = parseInt(expiresStr, 10);
    return Date.now() >= expiresAt - REFRESH_THRESHOLD;
  }

  /**
   * Store tokens securely
   */
  private storeTokens(tokens: AuthTokens, rememberMe: boolean = false): void {
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
    storage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    storage.setItem(EXPIRES_KEY, tokens.expiresAt.toString());
  }

  /**
   * Store user data
   */
  private storeUser(user: User, rememberMe: boolean = false): void {
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem(USER_KEY, JSON.stringify(user));
  }

  /**
   * Overwrite the stored user in whichever storage currently holds it, so the
   * header chip (which reads getUser(), not the query cache) reflects a profile
   * edit without changing whether the session was persisted.
   */
  updateStoredUser(user: User): void {
    const storage = localStorage.getItem(USER_KEY) !== null ? localStorage : sessionStorage;
    storage.setItem(USER_KEY, JSON.stringify(user));
  }

  /**
   * Clear all auth data
   */
  clearAuth(): void {
    [localStorage, sessionStorage].forEach((storage) => {
      storage.removeItem(ACCESS_TOKEN_KEY);
      storage.removeItem(REFRESH_TOKEN_KEY);
      storage.removeItem(USER_KEY);
      storage.removeItem(EXPIRES_KEY);
    });
  }

  /** Turn a TokenResponse into the stored token shape (expires_in is seconds). */
  private toTokens(payload: TokenResponse): AuthTokens {
    return {
      accessToken: payload.access_token,
      refreshToken: payload.refresh_token,
      expiresAt: Date.now() + payload.expires_in * 1000,
    };
  }

  /**
   * Log in against POST /auth/login, then read the profile from /auth/me —
   * the token response carries no user, and the role decides admin routing.
   */
  async login(credentials: LoginCredentials): Promise<{ user: User; tokens: AuthTokens }> {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: credentials.email, password: credentials.password }),
    });

    if (!response.ok) {
      // Attach the status so the login page can map 401 ("Invalid email or
      // password") to an Indonesian sentence while still surfacing the server's
      // own message for any other code.
      throw Object.assign(new Error(await readErrorMessage(response, 'Email atau password salah')), {
        status: response.status,
      });
    }

    const tokens = this.toTokens(await response.json());
    this.storeTokens(tokens, credentials.rememberMe);

    const user = await this.fetchProfile(tokens.accessToken);
    this.storeUser(user, credentials.rememberMe);

    return { user, tokens };
  }

  /** Read the signed-in user with an explicit token (used right after login). */
  private async fetchProfile(accessToken: string): Promise<User> {
    const response = await fetch(`${API_BASE}/auth/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!response.ok) {
      this.clearAuth();
      throw new Error(await readErrorMessage(response, 'Gagal memuat profil'));
    }
    return toUser(await response.json());
  }

  /**
   * Refresh access token
   */
  async refreshAccessToken(): Promise<AuthTokens> {
    // Prevent multiple simultaneous refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    // Whether the session was persisted is recoverable from where the refresh
    // token currently lives, so a refresh does not silently change that.
    const rememberMe = localStorage.getItem(REFRESH_TOKEN_KEY) !== null;

    this.refreshPromise = (async () => {
      try {
        const response = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (!response.ok) {
          throw new Error(await readErrorMessage(response, 'Sesi berakhir, silakan masuk lagi'));
        }

        const tokens = this.toTokens(await response.json());
        this.storeTokens(tokens, rememberMe);
        return tokens;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Log out.
   *
   * The API exposes no logout endpoint (see docs/api/openapi.json), so this
   * only drops local credentials; issued tokens stay valid until they expire.
   */
  async logout(): Promise<void> {
    this.clearAuth();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    if (!this.getUser()) return false;
    // A refresh token keeps the session alive: apiClient refreshes lazily on
    // the next request. Gating on access-token freshness here bounced users to
    // /login (REFRESH_THRESHOLD is 5 min) before any refresh could run.
    if (this.getRefreshToken()) return true;
    return !!this.getAccessToken() && !this.isTokenExpired();
  }

  /**
   * Get authorization header
   */
  getAuthHeader(): { Authorization: string } | Record<string, never> {
    const token = this.getAccessToken();
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }
}

export const authService = new AuthService();
export default authService;
